import { RegionType, } from "@/UI/Navigation/TreeView/TreeInterfaces";
import * as api from "@/Api/";
import { getMoleculesFromStore, setStoreVar, } from "@/Store/StoreExternalAccess";
import { selectProgramatically } from "@/UI/Navigation/TitleBar/MolSelecting";
import { store } from "@/Store";
export let loadViewerLibPromise = undefined;
import { toRaw } from "vue";
import { createAnimationFrameCoalescer, createLeadingEdgeThrottle } from "@/Core/Utils/CoalescedTask";
import { isVisualizationDeferred } from "@/Core/Utils/CoalescedTask";
/**
 * Sets the loadViewerLibPromise variable.
 *
 * @param  {Promise<any>} val  The promise to set.
 */
export function setLoadViewerLibPromise(val) {
    loadViewerLibPromise = val;
}
/**
 * The ViewerParent abstract class. Other viewers (e.g., 3dmoljs) inherit this
 * one.
 */
export class ViewerParent {
    // Keep track of which molecules have been loaded.
    molCache = {};
    // Keep track of the surfaces as well. Associating them with molecule ids.
    // Note that a given molecule can have multiple surfaces.
    surfaces = {};
    // Keep track of the regions.
    regionCache = {};
    // Keep track of region definitions to avoid unnecessary recreation
    regionDefinitionCache = {};
    // This function is called to add a class to the a div surrounding the
    // viewer. For example, to change the style on the cursor depending on
    // whether you're hovering over an atom.
    updateViewerDivClassCallback;
    /**
     * Monotonically increasing generation counter used to cancel stale zoom
     * animations. Each call to zoomOnFocused increments this; the concrete
     * zoomToModels implementation checks it to abort if a newer zoom has
     * been requested.
     */
    _zoomGeneration = 0;
    /**
     * Coalesces multiple render requests within the same animation frame into
     * a single repaint, avoiding redundant GPU work when many state changes
     * occur in quick succession (e.g., bulk show/hide, style updates).
     */
    _renderAnimFrameCoalescer = createAnimationFrameCoalescer(() => {
        this._renderAll();
    });
    /**
     * Coalesces multiple render requests into a single call at most once every
     * 500ms. During batch molecule loading, renderAll() is a no-op (guarded
     * by isRenderDeferred), so this throttle only governs interactive use.
     */
    _renderAllCoalescer = createLeadingEdgeThrottle(() => {
        this._renderAnimFrameCoalescer.invoke();
    }, 500);
    /**
     * Request a viewer render, coalesced via a leading-edge throttle and
     * requestAnimationFrame. Skipped entirely while visualization is
     * deferred.
     *
     * @returns {Promise<void>}
     */
    renderAll() {
        // During batch loading, suppress intermediate renders. The
        // BatchRenderGate will call updateStylesInViewer (which calls
        // renderAll) once when loading settles.
        if (isVisualizationDeferred()) {
            return Promise.resolve();
        }
        this._renderAllCoalescer.invoke();
        return Promise.resolve();
    }
    /**
     * Force an immediate render, bypassing the throttle and coalescer.
     * Used by deferVisualization() after the deferred work completes,
     * and by _updateStyleChanges at the end of a style-update pass.
     *
     * @returns {Promise<void>}
     */
    renderImmediate() {
        return this._renderAll();
    }
    /**
     * Remove all cached objects whose ids are not in the provided set of
     * remaining molecule ids.
     *
     * @param {Set<string> | string[]} remainingMolIds  The ids of molecules
     *     that should be kept. Accepts a Set for O(1) lookups when the
     *     caller already has one, or an array for backward compatibility.
     */
    removeObjects(remainingMolIds) {
        const idSet = remainingMolIds instanceof Set
            ? remainingMolIds
            : new Set(remainingMolIds);
        // Find the ids that are still present in the cache. These should be
        // removed.
        const idsOfMolsOrRegionsToDelete = [];
        for (const molCacheId in this.molCache) {
            if (!idSet.has(molCacheId)) {
                idsOfMolsOrRegionsToDelete.push(molCacheId);
            }
        }
        for (const regionCacheId in this.regionCache) {
            if (!idSet.has(regionCacheId)) {
                // There's an id in the cache that isn't in the tree.
                idsOfMolsOrRegionsToDelete.push(regionCacheId);
            }
        }
        if (idsOfMolsOrRegionsToDelete.length > 0) {
            // Remove them from the cache, viewer, etc.
            idsOfMolsOrRegionsToDelete.forEach((id) => {
                // It is strange, but it's important to hide before removing.
                // Never did figure out why.
                this.hideObject(id);
                this.removeObject(id);
            });
            this.renderAll();
        }
    }
    /**
     * Removes a single model or region from the viewer.
     *
     * @param {string} id  The id of the model or region to remove.
     */
    removeObject(id) {
        // Clear any surfaces
        this.clearSurfacesOfMol(id);
        if (this.molCache[id]) {
            this._removeModel(id);
        }
        if (this.regionCache[id]) {
            this._removeRegion(id);
        }
        // Remove from cache
        this.removeFromCache(id);
        // Note that not calling render here. Need to call it elsewhere for
        // these changes to appear in 3dmoljs viewer.
    }
    /**
     * Hide a model or region.
     *
     * @param  {string} id  The model or region to hide.
     * @returns {void}
     */
    hideObject(id) {
        const model = this.molCache[id];
        if (model) {
            this.hideMolecule(id);
            this._makeAtomsNotHoverableAndClickable(model);
            this.renderAll();
            return;
        }
        if (this.regionCache[id]) {
            this.hideRegion(id);
            this.renderAll();
        }
    }
    /**
     * Show a model or region.
     *
     * @param  {string} id  The id of the model or region to show.
     */
    showObject(id) {
        const model = this.molCache[id];
        if (model) {
            this.showMolecule(id);
            this._makeAtomsHoverableAndClickable(model, id);
            this.renderAll();
            return;
        }
        if (this.regionCache[id]) {
            // Get the original TreeNode to find the target opacity.
            const treeNode = getMoleculesFromStore().filters.onlyId(id);
            let opacity = 1;
            if (treeNode && treeNode.region && treeNode.region.opacity) {
                opacity = treeNode.region.opacity;
                this.createRegionLabel(id, treeNode.title);
            }
            this.showRegion(id, opacity);
            this.renderAll();
        }
    }
    _callbackRegistered = false;
    /**
     * Register a callback to be called when the view changes.
     *
     * @param  {Function} callback  The callback to register.
     * @returns {void}
     */
    registerViewChangeCallback(callback) {
        if (!this._callbackRegistered) {
            // Make sure registered only once.
            this._callbackRegistered = true;
            this._registerViewChangeCallback(callback);
        }
    }
    // abstract updateSurfaceStyle(id: string, style: GenericStyleType): void;
    /**
     * Adds a surface.
     *
     * @param {string}    id     The id of the model to add the surface to.
     * @param {GenericStyleType} style  The style of the surface.
     * @returns {Promise<GenericSurfaceType>}  A promise that resolves with the surface
     *     type when it's ready.
     */
    addSurface(id, style) {
        return this._addSurface(id, style).then((surface) => {
            // Add to surface cache
            this.surfaces[id] = this.surfaces[id] || [];
            this.surfaces[id].push(surface);
            return surface;
        });
    }
    /**
     * Adds a region to the viewer.
     *
     * @param  {IRegion} region   The region to add.
     * @returns {GenericRegionType}  The region that was added.
     */
    addRegion(region) {
        const regionFull = {
            color: "red",
            opacity: 0.8,
            ...region,
        };
        let genericRegion;
        switch (region.type) {
            case RegionType.Sphere: {
                genericRegion = this.addSphere(regionFull);
                break;
            }
            case RegionType.Box: {
                genericRegion = this.addBox(regionFull);
                break;
            }
            case RegionType.Arrow: {
                genericRegion = this.addArrow({
                    radius: 0.5,
                    radiusRatio: 1.618034,
                    ...regionFull,
                });
                break;
            }
            case RegionType.Cylinder: {
                genericRegion = this.addCylinder({
                    radius: 0.5,
                    dashed: false,
                    ...regionFull,
                });
                break;
            }
        }
        return genericRegion;
    }
    /**
     * Adds a list of tree nodes to the viewer. During batch loading, this
     * may be called once with many nodes (when the deferred render fires)
     * rather than once per molecule, significantly reducing 3Dmol.js
     * addModel overhead.
     *
     * @param {TreeNodeList} treeNodeList   The list of molecules to add.
     * @returns {Promise<TreeNode>[]}  A list of promises that resolve when the
     *    molecules are added.
     */
    addTreeNodeList(treeNodeList) {
        // Add all the models and put them in the cache.
        const addMolPromises = [];
        for (let idx = 0; idx < treeNodeList.length; idx++) {
            const treeNode = treeNodeList.get(idx);
            const id = treeNode.id;
            // If it's not in the cache, the system has probably not yet loaded the
            // molecule. Always load it.
            let addObjPromise;
            // TODO: Currently doesn't account for regions.
            if (this.molCache[id] || this.regionCache[id]) {
                // Already in molecule cache
                addObjPromise = Promise.resolve(treeNode);
            }
            else {
                // Not in cache.
                if (treeNode.model) {
                    // treeNode.model might be a proxy, so need to use toRaw.
                    // This should run first
                    addObjPromise = this.addModel(toRaw(treeNode.model))
                        .then((visMol) => {
                        this.molCache[id] = visMol;
                        // Below now handled elsewhere (when showMolecule or
                        // hideMolecule)
                        // this._makeAtomsHoverableAndClickable(visMol);
                        return treeNode;
                    })
                        .catch((err) => {
                        console.warn(`Skipping model for node "${treeNode.title}": ${err.message}`);
                        // Intentionally not rethrowing: a single failed
                        // model should not abort loading the remaining
                        // molecules in the batch.
                        // throw err;
                        return treeNode;
                    });
                }
                else if (treeNode.region) {
                    // Make the region as pending because otherwise sometimes a
                    // second copy of the region gets added before the promise
                    // resolves.
                    this.regionCache[id] = "pending";
                    addObjPromise = this.addRegion(treeNode.region).then((region) => {
                        this.regionCache[id] = region;
                        this.regionDefinitionCache[id] = treeNode.region;
                        // Hide it if it should be invisible.  Handled elsewhere
                        // (up chain).
                        // if (treeNode && !treeNode.visible) {
                        //     console.log("Hiding:" + treeNode.id)
                        //     this.hideRegion(treeNode.id as string);
                        // }
                        return treeNode;
                    });
                }
                else {
                    console.warn(`Skipping node "${treeNode.title}": no model or region.`);
                    addObjPromise = Promise.resolve(treeNode);
                }
            }
            addMolPromises.push(addObjPromise);
        }
        return addMolPromises;
    }
    /**
     * Subscribers notified when any atom is clicked in the 3D viewer.
     * Maintained here (not in subclasses) so the subscription API is
     * uniform across viewer implementations.
     */
    _atomClickedSubscribers = [];
    /**
     * Subscribe to atom-click events. The returned function unsubscribes.
     * Consumers (e.g. ProteinSequenceViewer) use this to react to 3D
     * clicks regardless of which concrete viewer is active.
     *
     * @param {AtomClickedCallback} callback  Invoked with atom metadata.
     * @returns {() => void}  Unsubscribe function.
     */
    onAtomClicked(callback) {
        this._atomClickedSubscribers.push(callback);
        return () => {
            const idx = this._atomClickedSubscribers.indexOf(callback);
            if (idx !== -1) {
                this._atomClickedSubscribers.splice(idx, 1);
            }
        };
    }
    /**
     * Fires the atom-click event to all subscribers. Subclasses call this
     * from their viewer-specific click handler. Errors in subscribers are
     * caught so one bad listener cannot break the others.
     *
     * @param {IClickedAtomInfo} info  Metadata about the clicked atom.
     */
    _fireAtomClicked(info) {
        for (const cb of this._atomClickedSubscribers) {
            try {
                cb(info);
            }
            catch (err) {
                console.warn("Atom click subscriber threw:", err);
            }
        }
    }
    /**
     * Makes atoms NOT responsive to mouse hovering and clicking.
     *
     * @param {GenericModelType} model  The model to make atoms NOT hoverable
     *                                  and clickable.
     */
    _makeAtomsNotHoverableAndClickable(model) {
        this.makeAtomsNotHoverable(model);
        this.makeAtomsNotClickable(model);
    }
    /**
     * Makes atoms responsive to mouse hovering and clicking.
     *
     * @param {GenericModelType} model    The model to make atoms hoverable and
     *                                    clickable.
     * @param {string}           modelID  The ID of the model.
     */
    _makeAtomsHoverableAndClickable(model, modelID) {
        this.makeAtomsClickable(model, (info) => {
            // Fan out to any external subscribers (e.g. the sequence viewer)
            // before running the default selection behavior. Subscribers
            // receive full atom metadata, not just coordinates.
            this._fireAtomClicked({ ...info, moleculeId: modelID });
            // Determine if any of the currently selected items are regions.
            const selectedRegions = getMoleculesFromStore()
                .filters.keepSelected(true, true)
                .filters.keepRegions(true, true);
            if (selectedRegions.length == 0) {
                // Region is not selected, so select the molecule.
                selectProgramatically(modelID);
                setStoreVar("clearFocusedMolecule", false);
            }
            else {
                // region is selected
                api.plugins.runPlugin("moveregionsonclick", [info.x, info.y, info.z]);
            }
        });
        this.makeAtomsHoverable(model, ( /* x: number, y: number, z: number */) => {
            if (this.updateViewerDivClassCallback) {
                this.updateViewerDivClassCallback("cursor-pointer");
            }
        }, () => {
            if (this.updateViewerDivClassCallback) {
                this.updateViewerDivClassCallback("cursor-grab");
            }
        });
    }
    /**
     * Cancel any in-flight or pending camera zoom. Bumps the zoom generation
     * so stale zoomToModels setups abort, and tells the concrete viewer to
     * stop its running motion. Invoked when a new zoom is issued and on any
     * user interaction with the viewer (rotate, pan, scroll-zoom), each of
     * which would otherwise compete with a running animation and jerk the
     * camera.
     */
    cancelZoom() {
        this._zoomGeneration++;
        this._cancelMotion();
    }
    /**
     * Zoom in on the focused molecules. Cancels any in-flight zoom first (which
     * also increments the zoom generation) so the new zoom does not fight a
     * still-running animation.
     *
     * @param {string[]} visibleTerminalNodeModelsIds  The visible models. If no
     *                                                 tree nodes are labeled as
     *                                                 focused, the function
     *                                                 will just use all visible
     *                                                 terminal nodes. If this
     *                                                 is undefined, will
     *                                                 determine from the tree
     *                                                 which are visible and
     *                                                 terminal.
     */
    zoomOnFocused(visibleTerminalNodeModelsIds) {
        // Cancel any in-flight zoom (and bump the generation) so the new zoom
        // does not fight a still-running animation.
        this.cancelZoom();
        let molsToFocusIds = [];
        const allMols = getMoleculesFromStore();
        const flatNodes = allMols.flattened;
        for (let idx = 0; idx < flatNodes.length; idx++) {
            const treeNode = flatNodes.get(idx);
            if (treeNode.focused) {
                if (!treeNode.nodes) {
                    // Already terminal
                    molsToFocusIds = [treeNode.id];
                }
                else {
                    const children = treeNode.nodes;
                    molsToFocusIds = children.filters.onlyTerminal.map((n) => n.id);
                }
                break;
            }
        }
        if (molsToFocusIds.length === 0) {
            // If nothing specified, focus on all visible molecules at once.
            if (visibleTerminalNodeModelsIds === undefined) {
                visibleTerminalNodeModelsIds = allMols.terminals.filters
                    .keepVisible()
                    .map((n) => n.id);
            }
            molsToFocusIds = visibleTerminalNodeModelsIds;
        }
        // this.renderAll();
        if (store.state["updateZoom"]) {
            this.zoomToModels(molsToFocusIds);
            // api.visualization.viewer.zoom(0.8);
        }
    }
    /**
     * Loads and sets up the viewer object.
     *
     * @param  {string}   id                            The HTML Dom id of the
     *                                                  element to load the
     *                                                  viewer into.
     * @param  {Function} updateViewerDivClassCallback  A callback that updates
     *                                                  the class of the
     *                                                  viewer-encompassing div.
     * @returns {Promise<GenericViewerType>}  A promise that resolves the viewer
     *    when it is loaded and set up.
     */
    loadAndSetupViewerLibrary(id, updateViewerDivClassCallback) {
        this.updateViewerDivClassCallback = updateViewerDivClassCallback;
        return this._loadAndSetupViewerLibrary(id);
    }
    /**
     * Converts a selection and style to the format compatible with this viewer.
     *
     * @param {ISelAndStyle} selAndStyle  The selection and style to convert.
     * @param {TreeNode}     treeNode     The molecular container, which may
     *                                    contain additional/more accessible
     *                                    information about the molecule than is
     *                                    available in the model itself.
     * @returns {object}  An object containing the converted selection and
     * style.
     */
    convertSelectionAndStyle(selAndStyle, treeNode) {
        // Convert the selection
        const selection = this.convertSelection(selAndStyle.selection);
        // Copy the ISelAndStyle object and remove the selection
        let style = { ...selAndStyle };
        delete style.selection;
        delete style.moleculeId; // This is for our internal use only
        // Convert the style
        style = this.convertStyle(style, treeNode);
        // return converted selection and style
        return { selection, style };
    }
    /**
     * Unloads the viewer and removes it from api.
     */
    unLoadViewer() {
        this._renderAnimFrameCoalescer.cancel();
        this._renderAllCoalescer.cancel();
        // Remove from api
        api.visualization.viewerObj = undefined;
        loadViewerLibPromise = undefined;
        // Do any viewer-specific unloading
        this.unLoad();
        // Remove 3dmoljs from dom
        const viewer = document.getElementById("mol-viewer");
        if (viewer) {
            viewer.innerText = "";
        }
        // All tree nodes are now dirty (so will be rerendered if new viewer
        // loaded).
        getMoleculesFromStore().flattened.forEach((treeNode) => {
            treeNode.viewerDirty = true;
        });
    }
    /**
     * A helper function that looks up a model or region in cache (in the
     * viewer-appropriate format) given a molecule container.
     *
     * @param  {string} id  The molecule or region id.
     * @returns {GenericModelType | undefined}  The model or region, or undefined if it
     *    is not in the cache.
     */
    lookup(id) {
        if (id === undefined) {
            return undefined;
        }
        if (this.molCache[id]) {
            // remove vuejs proxy
            return toRaw(this.molCache[id]);
        }
        return toRaw(this.regionCache[id]);
    }
    /**
     * Removes a model or region from the cache.
     *
     * @param {string} id  The id of the model or region to remove.
     */
    removeFromCache(id) {
        if (this.molCache[id]) {
            delete this.molCache[id];
        }
        if (this.regionCache[id]) {
            delete this.regionCache[id];
        }
        if (this.regionDefinitionCache[id]) {
            delete this.regionDefinitionCache[id];
        }
    }
    /**
     * Clear the surface associated with a molecule id.
     *
     * @param {string} id  The molecule id.
     */
    clearSurfacesOfMol(id) {
        if (id && this.surfaces[id]) {
            for (const surface of this.surfaces[id]) {
                this.removeSurface(surface);
                delete this.surfaces[id];
            }
        }
    }
    /**
     * Clear the cache of molecules, regions, and surfaces.
     */
    clearCache() {
        for (const id in this.molCache) {
            this.removeObject(id);
        }
        for (const id in this.regionCache) {
            this.removeObject(id);
        }
        this.renderAll();
    }
    /**
     * Checks if two arrays are equal.
     *
     * @param {any[]} a First array.
     * @param {any[]} b Second array.
     * @returns {boolean} True if equal.
     */
    areArraysEqual(a, b) {
        if (a === b)
            return true;
        if (a == null || b == null)
            return false;
        if (a.length !== b.length)
            return false;
        for (let i = 0; i < a.length; ++i) {
            if (a[i] !== b[i])
                return false;
        }
        return true;
    }
    /**
     * Gets the text to show when mouse hovers over an atom.
     *
     * @param {string} chain     The chain id.
     * @param {string} resn      The residue name.
     * @param {string} resi      The residue index.
     * @param {string} atomName  The atom name.
     * @returns {string | undefined}  The text to show, or undefined if no text
     *   should be shown.
     */
    hoverLabelText(chain, resn, resi, atomName) {
        const lbls = [];
        if (chain) {
            lbls.push(chain);
        }
        if (resn) {
            lbls.push(resn);
        }
        if (resi) {
            lbls.push(resi);
        }
        if (atomName) {
            lbls.push(atomName);
        }
        if (lbls.length > 0) {
            return lbls.join(":");
        }
        return undefined;
    }
}
